import { CellType } from "../core/types.ts";
import { GRID_WIDTH, GRID_HEIGHT, GAME } from "../core/config.ts";
import { PHASE_BUILD } from "../game/types.ts";
import type { GameContext } from "../game/types.ts";
import type { GameLoop } from "../engine/GameLoop.ts";
import type { Grid } from "../core/Grid.ts";
import { setupEventHandlers } from "../game/eventHandlers.ts";
import { glowCache } from "../rendering/GlowSpriteCache.ts";
import { FIREWALL_CAP } from "../entities/Firewall.ts";

export function resetForNewRun(
  ctx: GameContext,
  gameLoop: GameLoop,
  grid: Grid,
  recalculatePath: () => void,
  pauseOverlay: { hide(): void },
  triggerWaveStart: () => void,
): void {
  const { gs } = ctx;

  // CR-1: Dispose all EventBus listeners BEFORE any other reset
  ctx.disposeHandlers?.();

  gameLoop.stop();
  // BL-44: the governor subsumes the old WebGL frame-history clear AND restores
  // every actuator to full fidelity (level 0 → post-fx high, full particle cap,
  // motion 1) so a degraded run never leaks its degraded state into the next.
  ctx.perfGovernor.reset();
  ctx.gameOverScreen.hide();
  ctx.victoryScreen?.hide();
  // Dismiss any transient full-screen overlay still up at reset time. Without
  // this, restarting / exiting while a draft (or the skills screen, or a spawn
  // alert) is open leaves a pointer-events:all layer covering the fresh run —
  // every click is swallowed while keyboard (Space) still reaches `document`,
  // so the wave button and tower actions appear dead ("only Space works").
  if (ctx.draftUI.visible) ctx.draftUI.hide();
  if (ctx.skillOverview.visible) ctx.skillOverview.hide();
  ctx.spawnAlert.hide();
  // BL-141 — tear down the tower upgrade/sell panel (z-9999 fixture in
  // #game-container, not an overlay layer) so a RESTART from a state with a
  // tower selected starts on a clean board (gs.selectedTower is nulled below).
  ctx.towerSelector.hideTowerInfo();
  ctx.hud.resetFastForward();
  pauseOverlay.hide();
  ctx.hud.syncPauseState(false);

  // Reset RuntimeState to initial values
  gs.phase = PHASE_BUILD;
  gs.gold = GAME.INITIAL_CREDITS;
  gs.lives = GAME.INITIAL_HP;
  gs.wave = 0;
  gs.waveActive = false;
  gs.selectedTowerType = null;
  gs.placementMode = "tower";
  gs.towers.length = 0;
  gs.firewalls.length = 0;
  // D-322 / AC-5 (Task-007): FIREWALL_CAP is data-driven via `firewalls.json`
  // `maxFirewalls` (byte-safe fallback 3).
  gs.maxFirewalls = FIREWALL_CAP;
  gs.timeWarpAvailable = false;
  gs.timeWarpDuration = 5;
  gs.recursiveLoopThreshold = 0;
  gs.recursiveLoopCounter = 0;
  gs.globalDamageMult = 1;
  gs.resonanceSynergyEnabled = false;
  gs.bossHpMult = 1;
  gs.bossHealingDisabled = false;
  gs.skillsCostZero = false;
  gs.firewallSlowActive = false;
  gs.firewallSlowFactor = 1.0;
  gs.showAllHPBars = false;
  gs.targetingMode = "default";
  gs.previousPhase = null;
  gs.selectedTower = null;
  gs.hoveredTower = null; // S64-F05: clear stale hover reference
  ctx.failsafeAvailable = false;
  gs.runStats.enemiesKilled = 0;
  gs.runStats.towersBuilt = 0;
  gs.runStats.goldEarned = 0;
  gs.runStats.goldSpent = 0;
  gs.perfectStreak = 0;
  gs.isEndlessMode = false;
  gs.waveCountdown = 0;
  gs.waveStartedThisCountdown = false;
  gs.logicBombs = [];
  gs.payloadTargetingMode = null;
  gs.payloadTargetingCard = null;
  gs.maxLives = 20;
  gs.cascadeActive = false;
  gs.cascadeDamage = 0;
  gs.campaignLivesBonus = 0;

  // Reset systems
  ctx.waveManager.reset();
  ctx.combatSystem.reset();
  ctx.projectilePool.releaseAll();
  ctx.draftSystem.reset();
  ctx.payloadRack.reset();
  // BL-70: clear payload targeting + logic bombs
  gs.logicBombs.length = 0;
  gs.payloadTargetingMode = null;
  gs.payloadTargetingCard = null;
  ctx.scoreSystem.reset();
  ctx.synergySystem.reset();
  ctx.breachSystem.reset();
  ctx.floatingDmg.clear();
  ctx.gameBanner.reset();
  ctx.scannerReadout.dismiss();
  ctx.pathRevealAnimation.reset();
  ctx.spawnDetectionSystem.reset();
  ctx.gridRenderer.bossPathColor?.reset();
  // P2-6: release the previous run's cached glow sprites (bounded LRU, but this
  // frees its OffscreenCanvases between runs on a tight phone). Safe: glows are
  // re-rasterised lazily on first use in the new run (one-time, cheap).
  glowCache.clear();

  // Economy is the canonical gold balance; gs.gold is the render mirror.
  ctx.economy.reset(GAME.INITIAL_CREDITS);
  gs.gold = GAME.INITIAL_CREDITS;

  // Reset grid (clear placed towers/firewalls)
  for (let gx = 0; gx < GRID_WIDTH; gx++) {
    for (let gy = 0; gy < GRID_HEIGHT; gy++) {
      const cell = grid.getCell(gx, gy);
      if (
        cell &&
        (cell.type === CellType.TOWER || cell.type === CellType.WALL)
      ) {
        grid.setCell(gx, gy, CellType.EMPTY);
      }
    }
  }
  recalculatePath();
  ctx.waveManager.pathPixels = gs.pathPixels;

  // Reset UI
  ctx.hud.update(gs);
  ctx.waveIndicator.show(gs.wave + 1, triggerWaveStart);
  // Clear any leftover countdown number from the previous run (the frozen "9").
  ctx.waveIndicator.setCountdown(0, gs.wave);
  ctx.skillDeckBar.update([...ctx.draftSystem.cards]);
  ctx.liveBars?.refresh(); // BL-56b: re-read the cleared wave/payload state (→ 0)
  ctx.enemyReveal.reset(); // BL-48: clear seen-types + dismiss any in-flight card
  ctx.hud.resetComms(); // S64-F06: clear run-scoped bark counters
  // Nothing selected on a fresh grid (no tower preselected).
  ctx.towerSelector.selectedType = null;

  // CR-1: Re-subscribe fresh EventBus handlers after all state is clean
  ctx.disposeHandlers = setupEventHandlers(ctx, gameLoop);
}
